import { axios } from '@nuskin/axios-util';
import sanitizeHTML from 'sanitize-html';
import CONFIG from './addressConfig';
import validMarkets from './validationMarkets.json';

const MAX_RETRIES = 4;

const axiosCreate = axios.create || axios.default.create
const myAxios = axiosCreate();

const mixin = {
    props: {
        value: {
            type: Object,
            default() {
                return {};
            },
        },
        lang: {
            type: String,
            default() {
                return 'en';
            },
        },
        includeEmail: {
            type: Boolean,
            required: true,
        },
        emailRequired: Boolean,
    },
    data() {
        return {
            address: { ...this.value },
            addressValid: false,
            suggestedAddresses: [],
            modalOpen: false,
            formAddress: {},
            address1Rules: [],
            address2Rules: [],
            basicCharacterInputRule: [],
            cityRules: [],
            stateRules: [],
            postalCodeRules: [],
            emailRules: [],
            requiredRule: [],
            t: {},
        };
    },
    watch: {
        t() {
            if (Object.keys(this.t).length > 0) {
                this.initRules();
            }
        },

        value() {
            this.address = { ...this.value };
            this.$emit('new-address-in-form');
        },
    },
    methods: {
        addressConfirmed(v) {
            const correctedAddress = Object.assign({ address1: v.street }, { ...v });
            this.$emit('input', { ...correctedAddress });
            this.changeModal(false);

            // set to valid due to user input selecting a suggested address
            this.addressValid = true;

            this.$emit('suggested-addresses-updated', []);
        },

        addressClose() {
            this.changeModal(false);
        },

        addressUpdated() {
            // reset address to invalid when a user change address
            // after validation occurred
            this.addressValid = false;

            this.$emit('input', { ...this.address });
        },

        onBlur() {
            this.$emit('blur', { ...this.address });
        },

        changeModal(v) {
            this.modalOpen = v;
        },

        async validateAddress(country, clientId) {
            // We short circuit since form is already valid
            if (this.addressValid) {
                return;
            }

            // Trim whitespace on all address fields
            const addressFields = Object.keys(this.address || {});
            if (addressFields.length > 0) {
                addressFields.forEach((field) => {
                    if (this.address[field] && typeof this.address[field] === 'string') {
                        this.address[field] = this.address[field].trim();
                    }
                });
            }

            const result = await this.getSuggestedAddresses(country, clientId);

            // Select input address when there is an exact match returned by api
            if (result.exactMatch) {
                this.addressConfirmed(result.input);
            } else {
                this.changeModal(true);
            }
        },

        checkRegion(region) {
            let newRegion;
            switch (region) {
                case 'CHP':
                    newRegion = 'CHI';
                    break;
                case 'GRO':
                    newRegion = 'GUE';
                    break;
                case 'QROO':
                    newRegion = 'QR';
                    break;
                default:
                    newRegion = region;
                    break;
            }
            return newRegion;
        },

        async getSuggestedAddresses(country, clientId) {
            const domain =
        window.location.hostname === 'www.nuskin.com'
            ? 'apis.nuskin.com'
            : 'apis.test.nuskin.com';

            if (validMarkets[country]) {
                const config = {
                    method: 'POST',
                    url: `https://${domain}/street-address/validate/v1/${country}?required=district`,
                    data: Object.assign(
                        { ...this.address },
                        {
                            street: this.address.address1,
                            street2: this.address.address2 || '',
                        },
                    ),
                };

                try {
                    const response = await myAxios(config)
                        .then((result) => {
                            return result.data;
                        })
                        .catch((error) => {
                            // console.error(error)
                            const result = { data: {} };
                            if (
                                Object.prototype.hasOwnProperty.call(error, 'config') &&
                error.config['axios-retry'].retryCount === MAX_RETRIES
                            ) {
                                result.data.message = 'Max retries reached.';
                            } else {
                                result.data.message = 'Request timeout.';
                            }

                            return result;
                        });

                    const validated =
            response && response.verificationStatus === 'Verified';
                    const candidates = response && response.candidates;
                    const input = response && response.input;

                    let suggestedAddresses = [];
                    let exactMatch = false;

                    if (candidates && candidates.length > 0) {
                        suggestedAddresses = candidates.map((candidate) => {
                            candidate.region = this.checkRegion(candidate.region);
                            return {
                                ...candidate,
                                address1: candidate.street,
                            };
                        });
                        exactMatch = this.isAddressExactMatch(suggestedAddresses, input);
                    }

                    // Always update modal content
                    this.$emit('suggested-addresses-updated', suggestedAddresses);
                    return {
                        validated,
                        addresses: suggestedAddresses,
                        exactMatch,
                        input,
                    };
                } catch (err) {
                    /* istanbul ignore next */
                    // eslint-disable-next-line no-console
                    console.error('Error fetching suggested addresses', err);
                    return {
                        validated: false,
                        addresses: [],
                        exactMatch: false,
                    };
                }
            }
        },

        /**
     * Check if single candidate returned by api and is an exact match to user input
     * @param {Array} suggestedAddresses
     * @param {Object} input
     */
        isAddressExactMatch(suggestedAddresses, input) {
            let exactMatch = false;

            if (suggestedAddresses.length === 1) {
                exactMatch = true;
                const suggestedAddress = suggestedAddresses[0];
                const fullInputAddress = `${input.street} ${input.street2}`;

                // Check each address field to match
                const fields = Object.keys(suggestedAddress || {});
                fields.forEach((field) => {
                    if (suggestedAddress[field] !== input[field]) {
                        // For addresses, look at street2 in the case we don't have an immediate match
                        // Api does not return street2, so must concatenate and check
                        if (
                            (field === 'address1' || field === 'street') &&
              input.street2 &&
              input.street2.length > 0 &&
              fullInputAddress === suggestedAddress[field]
                        ) {
                            return;
                        }
                        exactMatch = false;
                    }
                });
            }

            return exactMatch;
        },

        initRules() {
            const requiredRule = (v) => {
                const validation = (!!v && !!v.trim()) || this.t.fieldRequired;
                // console.debug('requiredRule validation: ', validation);
                return validation;
            };

            const illegalCharactersRule = (v) => {
                let illegalRegex = /^(?=.*[a-zA-Z0-9.!@#&*\-\u0080-\u052F])[a-zA-Z0-9\s.!@#&*'/(),\-\u0080-\u052F]*$/;
                if (
                    CONFIG[this.address.country] && CONFIG[this.address.country].illegalCharactersRegex
                ) {
                    illegalRegex = CONFIG[this.address.country].illegalCharactersRegex;
                }
                const validation = !v || illegalRegex.test(v.trim()) || this.t.illegalCharacters;
                // console.debug('illegalCharactersRule validation: ', validation);
                return validation;
            };

            const postalCodeRule = (v) => {
                const regEx = CONFIG[this.address.country] && CONFIG[this.address.country].postCodeRegex;
                const validation = !v || (regEx ? regEx.test(v.trim()) : true) || this.t.invalidPostalCode;
                // console.debug('postalCodeRule validation: ', validation, String(regEx));
                return validation;
            };

            const emailRule = (v) => {
                let validation = true;
                if (this.emailRequired) {
                    validation = requiredRule(v);
                }
                if (validation === true) {
                    validation =
                        !v ||
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                            .test(v) ||
                    this.t.invalidEmail;
                }
                return validation;
            };

            this.address1Rules = this.cityRules = [
                requiredRule,
                illegalCharactersRule,
            ];
            this.address2Rules = [illegalCharactersRule];
            this.basicCharacterInputRule = [illegalCharactersRule];
            this.postalCodeRules = [requiredRule, postalCodeRule];
            this.emailRules = [emailRule];
            this.stateRules = [requiredRule, illegalCharactersRule];
            this.requiredRule = [requiredRule]; // for state dropdown fields
        },
        sanitizeHTMLCode(html) {
            return sanitizeHTML(html);
        },
    },
};

export default mixin;
